/**
 * Free voice output for the chatbot — neural-first.
 *
 * PRIMARY engine: the self-hosted neural TTS behind POST /api/tts (piper-tts
 * on this dev box today, per docs/VOICE-1-endpoint.md; swaps to Chatterbox/
 * Indic-Parler-TTS/IndicF5 on the founder's GPU later — see docs/VOICE-3-
 * verdict.md — via the same VOICE_INFERENCE_URL contract). When that endpoint
 * is configured the server streams back audio and we play it — audio never
 * leaving founder-controlled infra, $0 per call once running.
 *
 * FALLBACK engine: the browser Web Speech API (`speechSynthesis`) — used ONLY
 * when the server returns {fallback:true} (no neural endpoint configured) or
 * the fetch/playback fails, so the "Listen" button never dies into silence.
 *
 * This module is the seam: callers (SpeakButton, VoiceScreen, …) call `speak()`
 * and never see which backend served the audio. Contract preserved from BD3.1 —
 * `speak()` returns a synchronous SpeakHandle; `onEnd` fires on natural finish,
 * error, OR cancel; one voice at a time.
 *
 * Adopted from the voice-tts-selfhost branch (2026-07-12) rather than rebuilt
 * from scratch — richer onboarding-language detection and autoplay-failure
 * handling than an initial pass here had. See docs/VOICE-0-rule-change.md for
 * why self-hosted voice is allowed in the main composer at all.
 */
import { getAuthToken } from "../screens/ocrpro/realApi";
import { readStoredAnswers } from "./storedAnswers";

/**
 * The voice language from the user's onboarding pick (localStorage). Empty ⇒
 * English (no translation). Two languages incl. English ⇒ a code-mix signal
 * ("hi-en" = Hinglish) so the self-host model can mix. The on-screen text stays
 * English regardless — only the spoken audio is in the chosen language.
 */
function voiceLang(): string {
  try {
    const a = readStoredAnswers() as { languages?: unknown } | null;
    const langs = Array.isArray(a?.languages)
      ? (a!.languages as unknown[]).map((x) => String(x).toLowerCase()).filter(Boolean)
      : [];
    const nonEn = langs.filter((l) => l !== "en" && l !== "english");
    if (nonEn.length === 0) return "";
    if (langs.length >= 2 && langs.some((l) => l === "en" || l === "english")) {
      return `${nonEn[0]}-en`;
    }
    return nonEn[0];
  } catch {
    return "";
  }
}

let currentUtterance: SpeechSynthesisUtterance | null = null;
let currentAudio: HTMLAudioElement | null = null;
let currentGen = 0; // bumped on every speak()/stop — invalidates in-flight fetch/play

export function ttsSupported(): boolean {
  // Neural TTS is just <audio>; browser speech is the fallback. If we're in a
  // browser at all, we can speak one way or the other.
  return (
    typeof window !== "undefined" &&
    ("speechSynthesis" in window || "Audio" in window)
  );
}

export function isSpeaking(): boolean {
  if (currentAudio && !currentAudio.paused && !currentAudio.ended) return true;
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    window.speechSynthesis.speaking
  );
}

export function stopSpeaking(): void {
  currentGen++; // any in-flight fetch / queued playback is now stale
  if (currentAudio) {
    try {
      currentAudio.pause();
    } catch {
      /* ignore */
    }
    try {
      if (currentAudio.src.startsWith("blob:")) URL.revokeObjectURL(currentAudio.src);
    } catch {
      /* ignore */
    }
    currentAudio = null;
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
  currentUtterance = null;
}

/**
 * Strip markdown down to speakable prose. Tables are summarised into a
 * pointer ("the comparison table is on screen") rather than read cell-by-cell
 * — listening to 40 pipe-separated values is worse than silence.
 */
export function stripMarkdownForSpeech(md: string): string {
  let out = md;
  // Fenced code blocks → skip entirely.
  out = out.replace(/```[\s\S]*?```/g, " ");
  // GFM table blocks (2+ consecutive |-rows) → one spoken pointer.
  out = out.replace(
    /(?:^\|.*\|\s*$\n?){2,}/gm,
    " There's a comparison table on screen with the details. ",
  );
  // Links [text](url) → text; images gone.
  out = out.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  // Headings / emphasis / inline code / list markers / rules.
  out = out.replace(/^#{1,6}\s*/gm, "");
  out = out.replace(/(\*\*|__|\*|_|`|~~)/g, "");
  out = out.replace(/^\s*[-*+]\s+/gm, "");
  out = out.replace(/^\s*\d+\.\s+/gm, "");
  out = out.replace(/^---+\s*$/gm, " ");
  return out.replace(/\s{2,}/g, " ").trim();
}

/** Prefer an Indian-English (then Hindi, then any English) system voice. */
function pickVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices() || [];
  return (
    voices.find((v) => v.lang === "en-IN") ||
    voices.find((v) => v.lang === "hi-IN") ||
    voices.find((v) => v.lang.startsWith("en")) ||
    voices[0] ||
    null
  );
}

export interface SpeakHandle {
  cancel: () => void;
}

/**
 * Ask the self-host neural TTS (POST /api/tts) to synthesize `prose`. Resolves
 * to a ready-to-play <audio> element, or null when the server has no neural
 * endpoint ({fallback:true}) or the request fails — caller then uses browser.
 */
async function ttsNeural(prose: string, gen: number): Promise<HTMLAudioElement | null> {
  if (typeof window === "undefined" || !("Audio" in window)) return null;
  try {
    const token = getAuthToken();
    const lang = voiceLang();
    const r = await fetch("/api/tts", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(lang ? { text: prose, lang } : { text: prose }),
    });
    if (gen !== currentGen) return null; // superseded while fetching
    const ct = r.headers.get("content-type") || "";
    if (!r.ok || !ct.includes("audio")) return null; // {fallback:true} or error → browser
    const blob = await r.blob();
    if (gen !== currentGen) return null;
    return new Audio(URL.createObjectURL(blob));
  } catch {
    return null;
  }
}

/** Browser Web Speech fallback — only reached when neural is unavailable. */
function speakBrowser(prose: string, gen: number, done: () => void): void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    done();
    return;
  }
  const u = new SpeechSynthesisUtterance(prose);
  const voice = pickVoice();
  if (voice) {
    u.voice = voice;
    u.lang = voice.lang;
  }
  u.rate = 1;
  u.onend = () => {
    if (currentUtterance === u) currentUtterance = null;
    if (gen === currentGen) done();
  };
  u.onerror = () => {
    if (currentUtterance === u) currentUtterance = null;
    if (gen === currentGen) done();
  };
  currentUtterance = u;
  window.speechSynthesis.speak(u);
}

/**
 * Speak `text` (markdown accepted — stripped internally). Tries the self-host
 * neural voice first, falls back to browser speech. Any previous speech is
 * cancelled first — one voice at a time. `onEnd` fires on natural finish, error,
 * AND cancel, so callers can reset their button state either way. Returns a
 * synchronous handle even though synthesis is async.
 */
export function speak(text: string, onEnd?: () => void): SpeakHandle | null {
  if (!ttsSupported()) return null;
  const prose = stripMarkdownForSpeech(text);
  if (!prose) return null;
  stopSpeaking();
  const gen = currentGen;
  const done = () => {
    if (gen === currentGen) onEnd?.();
  };

  void (async () => {
    // PRIMARY: self-host neural TTS.
    const el = await ttsNeural(prose, gen);
    if (gen !== currentGen) return; // cancelled while fetching
    if (el) {
      currentAudio = el;
      el.onended = () => {
        if (gen === currentGen) currentAudio = null;
        done();
      };
      el.onerror = () => {
        if (gen !== currentGen) return;
        currentAudio = null;
        speakBrowser(prose, gen, done);
      };
      try {
        await el.play();
        return; // neural is playing
      } catch {
        if (gen !== currentGen) return;
        currentAudio = null; // autoplay/policy failure → browser fallback below
      }
    }
    // FALLBACK: browser speechSynthesis.
    if (gen !== currentGen) return;
    speakBrowser(prose, gen, done);
  })();

  return { cancel: stopSpeaking };
}
